import { useEffect, useMemo, useRef, useState } from 'react';
import type { MemberOption } from '@catalyst-psa/shared';

import { useMemberOptions } from './api.js';

/** Teams derive from the member-record checkboxes of the same names. */
const TEAMS = [
  { key: 'isProjectManager', label: 'Project Manager' },
  { key: 'isSalesRep', label: 'Sales Rep' },
  { key: 'isTechnical', label: 'Technical' },
  { key: 'isFunctional', label: 'Functional' },
] as const satisfies ReadonlyArray<{ key: keyof MemberOption; label: string }>;

/**
 * Multi-select member filter: a dropdown button revealing team quick-picks (checking a
 * team selects a snapshot of every member currently flagged with it) plus a searchable
 * member checkbox list. Empty selection = no filtering (the "All …" label).
 */
export function MemberMultiFilter({
  selected,
  onChange,
  allLabel = 'All members',
  restrict,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel?: string;
  /** Optionally limit the offered members (e.g. sales reps only). */
  restrict?: (member: MemberOption) => boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const optionsQuery = useMemberOptions();

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const options = useMemo(() => {
    const all = optionsQuery.data ?? [];
    return restrict ? all.filter(restrict) : all;
  }, [optionsQuery.data, restrict]);

  const teams = useMemo(
    () =>
      TEAMS.map((team) => ({
        ...team,
        memberIds: options.filter((m) => m[team.key] === true).map((m) => m.id),
      })).filter((team) => team.memberIds.length > 0),
    [options],
  );

  const visibleOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((m) => m.displayName.toLowerCase().includes(needle));
  }, [options, search]);

  const labelOf = (id: string): string =>
    options.find((m) => m.id === id)?.displayName ?? 'Former member';

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length <= 2
        ? selected.map(labelOf).join(', ')
        : `${selected.length} members`;

  const toggleMember = (id: string, checked: boolean): void => {
    onChange(checked ? [...selected, id] : selected.filter((v) => v !== id));
  };

  const toggleTeam = (memberIds: string[], checked: boolean): void => {
    if (checked) {
      onChange([...new Set([...selected, ...memberIds])]);
    } else {
      const drop = new Set(memberIds);
      onChange(selected.filter((id) => !drop.has(id)));
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-[12rem] items-center justify-between gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-left text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        <span className="truncate">{summary}</span>
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 max-h-80 w-72 overflow-auto rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <button
            type="button"
            onClick={() => onChange([])}
            className="mb-1 w-full rounded-md border-b border-slate-100 px-2 py-1.5 text-left text-xs font-medium text-slate-500 hover:bg-slate-50"
          >
            Clear ({allLabel.toLowerCase()})
          </button>
          {teams.length > 0 ? (
            <div className="mb-1 border-b border-slate-100 pb-1">
              <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Teams
              </p>
              {teams.map((team) => {
                const allSelected = team.memberIds.every((id) => selected.includes(id));
                return (
                  <label
                    key={team.key}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleTeam(team.memberIds, e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span>{team.label}</span>
                    <span className="ml-auto text-xs text-slate-400">{team.memberIds.length}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members…"
            className="mb-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          {optionsQuery.isLoading ? (
            <p className="px-2 py-1.5 text-sm text-slate-400">Loading…</p>
          ) : visibleOptions.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-slate-400">No members match.</p>
          ) : (
            visibleOptions.map((member) => (
              <label
                key={member.id}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(member.id)}
                  onChange={(e) => toggleMember(member.id, e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="truncate">{member.displayName}</span>
              </label>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
